import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { FileText, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, permanent self-signup page for new agents. Anyone with the
 * link can complete + sign the Producer Agreement — the record lands
 * in the admin pipeline at `agreement_signed` for countersign.
 */
export default function AgentApply() {
  const [form, setForm] = useState({
    email: "",
    agency_name: "",
    effective_date: new Date().toISOString().slice(0, 10),
    legal_name: "",
    signatory_name: "",
    signatory_title: "",
    notice_address: "",
  });
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/signup-config`);
        setPdfUrl(data.agent_agreement_pdf_url);
      } catch { /* fallback below */ }
    })();
  }, []);

  const submit = async () => {
    if (!sig) { toast.error("Please draw your signature"); return; }
    for (const k of ["email", "agency_name", "effective_date", "legal_name", "signatory_name", "signatory_title", "notice_address"]) {
      if (!form[k] || !String(form[k]).trim()) { toast.error("Please complete every field"); return; }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/agents/self-apply`, { ...form, signature_data_url: sig });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <FullScreen>
        <Card className="max-w-lg w-full p-10 bg-white border border-[#E9ECEF] rounded-sm text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Received</div>
          <h1 className="mt-2 text-2xl font-black text-[#0B132B]">Thanks — we have your signed agreement</h1>
          <p className="mt-3 text-sm text-[#6C757D] leading-relaxed">
            Advantage Warranty Group will countersign shortly and you'll receive a welcome email at <strong className="text-[#0B132B]">{form.email}</strong> with your login link and onboarding next steps.
          </p>
        </Card>
      </FullScreen>
    );
  }

  const finalPdfUrl = pdfUrl || `${API}/public/signup/agent-agreement.pdf`;

  return (
    <FullScreen>
      <div className="max-w-4xl w-full">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            <ShieldCheck size={13} /> Producer Agreement
          </div>
          <h1 className="mt-2 text-4xl font-black text-[#0B132B] tracking-tight">Join Advantage Warranty Group</h1>
          <p className="mt-3 text-sm text-[#6C757D] max-w-xl mx-auto leading-relaxed">
            Complete the fields below, review the Producer Agreement, and sign to join our producer network. We'll countersign within one business day and send your login credentials by email.
          </p>
        </div>

        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] flex items-center gap-2">
              <FileText size={14} className="text-[#D4AF37]" /> Producer agreement
            </div>
            <a href={finalPdfUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#0B132B] hover:underline flex items-center gap-1">
              Open in new tab <ExternalLink size={12} />
            </a>
          </div>
          <iframe
            title="Producer Agreement"
            src={`${finalPdfUrl}#toolbar=0&navpanes=0`}
            className="w-full h-[500px] border border-[#E9ECEF] rounded-sm bg-white"
          />
        </Card>

        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-6">Your details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@youragency.com" data-testid="agent-apply-email" />
            </div>
            <div>
              <Label>Agency name</Label>
              <Input value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} data-testid="agent-apply-agency" />
            </div>
            <div>
              <Label>Effective date</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="agent-apply-effective" />
            </div>
            <div>
              <Label>Legal name (business entity)</Label>
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="e.g. Acme Warranty Partners LLC" data-testid="agent-apply-legal" />
            </div>
            <div>
              <Label>Signatory name</Label>
              <Input value={form.signatory_name} onChange={(e) => setForm({ ...form, signatory_name: e.target.value })} data-testid="agent-apply-signatory-name" />
            </div>
            <div>
              <Label>Signatory title</Label>
              <Input value={form.signatory_title} onChange={(e) => setForm({ ...form, signatory_title: e.target.value })} placeholder="e.g. Owner, Managing Partner" data-testid="agent-apply-signatory-title" />
            </div>
            <div className="md:col-span-2">
              <Label>Notice address</Label>
              <Textarea rows={2} value={form.notice_address} onChange={(e) => setForm({ ...form, notice_address: e.target.value })} placeholder="Address where legal notices should be sent" data-testid="agent-apply-notice" />
            </div>
            <div className="md:col-span-2">
              <Label>Signature</Label>
              <SignaturePad onChange={setSig} testId="agent-apply-signature-pad" />
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E9ECEF] flex items-center justify-between gap-4">
            <p className="text-xs text-[#6C757D] leading-relaxed">
              By clicking "Sign agreement", you acknowledge you have read the Producer Agreement above and agree to be bound by its terms upon our countersignature.
            </p>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-11 px-6 whitespace-nowrap"
              data-testid="agent-apply-submit"
            >
              {submitting ? "Submitting…" : "Sign agreement"}
            </Button>
          </div>
        </Card>
      </div>
    </FullScreen>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      {children}
    </div>
  );
}
